import { Home, Building2, UtensilsCrossed, Baby, Scissors, Users, Key, Bed } from "lucide-react";

/**
 * B2B vertical landing pages.
 * Each entry powers one page at /for/:slug — designed for cold email + Google Ads targeting.
 * Data-driven so we can add new verticals in one place without touching layout code.
 */
export const b2bVerticals = {
  realtors: {
    slug: "realtors",
    code: "B2B-01",
    Icon: Home,
    // Hero
    label: "For Realtors",
    h1: "Realtor's Termite Inspector in Pittsburgh",
    tagline: "24-hour WDI reports. Written same day. Deal saved.",
    metaTitle: "Realtor Termite Inspections Pittsburgh · 24hr Turnaround · Blue Collar World",
    metaDescription: "PA-licensed WDI inspections for Pittsburgh realtors. $75 flat rate, 24-hour written reports, same-week treatment if needed. Call 484-764-3392.",
    hero:
      "Every Pittsburgh sale needs a Wood-Destroying Insect inspection. If yours flags problems, buyers walk. We do WDI inspections with 24-hour written reports and same-week treatment — so your deal closes on time.",
    // Value props
    valueProps: [
      { title: "24-Hour Turnaround", body: "Inspection today, written report tomorrow. Same-day rush available." },
      { title: "$75 Flat Rate", body: "No hidden fees. No 'per square foot' upcharge. Just a clean invoice for your file." },
      { title: "Deal-Saving Treatment", body: "If WDI flags active infestation, we treat same week and issue clean re-certification." },
      { title: "PA License #BU15680", body: "Every report accepted by lenders, title companies and PA closing offices." },
    ],
    // Painpoints
    painpoints: [
      "Buyer's inspector flags termites two days before closing",
      "Other exterminators can't get out for 3 weeks",
      "Written reports arrive too late for closing table",
      "Getting nickel-and-dimed on inspection pricing",
    ],
    // Testimonial
    testimonial: {
      quote:
        "I use them on every WDI I need. Report is always in my inbox next morning. Twice they've saved a closing when other guys said 'next month.' My clients get a real answer, fast.",
      author: "Realtor · Mt. Lebanon",
    },
    // Custom form fields
    formFields: {
      companyLabel: "Brokerage",
      volumeLabel: "Approximate inspections per year",
      volumeOptions: ["1-5", "5-15", "15-30", "30+"],
    },
    ctaHeadline: "Add us to your inspector rolodex.",
    ctaSub: "One call. 24-hour reports. Your closings stay on schedule.",
  },

  "property-managers": {
    slug: "property-managers",
    code: "B2B-02",
    Icon: Building2,
    label: "For Property Managers",
    h1: "Multifamily Pest Control in Pittsburgh",
    tagline: "Bulk pricing. Recurring service. One point of contact.",
    metaTitle: "Multifamily Pest Control Pittsburgh · Property Manager Programs · Blue Collar World",
    metaDescription: "Quarterly and monthly pest control programs for Pittsburgh multifamily, apartment complexes, condos, HOAs. Bulk pricing, one invoice, one contact. Call 484-764-3392.",
    hero:
      "Property managers need one thing: a pest control vendor who shows up on schedule, handles tenant complaints without drama, and sends one clean invoice. That's us. Programs for buildings from 6 to 600 units across Allegheny County.",
    valueProps: [
      { title: "Bulk Unit Pricing", body: "Per-unit rates drop significantly at 10+ units. Custom quotes for 100+ door portfolios." },
      { title: "Tenant Coordination", body: "We handle scheduling and notifications with tenants directly. You stay out of the middle." },
      { title: "One Invoice, One Portal", body: "Monthly consolidated billing. Full service logs available for compliance." },
      { title: "Emergency Response", body: "Same-day for bed bugs, roach outbreaks, rodent activity. 24/7 line answered by a human." },
    ],
    painpoints: [
      "Tenant complaints piling up while your vendor 'schedules' the visit",
      "Getting stuck coordinating between 40 tenants and one exterminator",
      "Bed bug outbreak in one unit spreading to neighbors",
      "Compliance paperwork missing when it's audit time",
    ],
    testimonial: {
      quote:
        "We switched from a national chain — response time cut in half, invoice half the pages, and tenants stopped calling me about it. Should have done it years ago.",
      author: "Property Manager · 180-unit Pittsburgh complex",
    },
    formFields: {
      companyLabel: "Management Company",
      volumeLabel: "Total units under management",
      volumeOptions: ["1-25", "25-100", "100-300", "300+"],
    },
    ctaHeadline: "Cleaner buildings. Fewer calls.",
    ctaSub: "Request a portfolio-wide proposal. We'll come out and walk 2-3 buildings free.",
  },

  restaurants: {
    slug: "restaurants",
    code: "B2B-03",
    Icon: UtensilsCrossed,
    label: "For Restaurants",
    h1: "Restaurant Pest Control in Pittsburgh",
    tagline: "Night service. Health-code ready. Zero drama.",
    metaTitle: "Restaurant Pest Control Pittsburgh · Health Code Compliant · Blue Collar World",
    metaDescription: "Discreet pest control for Pittsburgh restaurants, bars, cafés. After-hours service, health inspection prep, roach + rodent + fruit fly programs. Call 484-764-3392.",
    hero:
      "One roach on a dining room floor and Yelp does the rest. We service Pittsburgh restaurants, bars, and cafés on your schedule — after hours, before the health inspector shows up, or on a locked-in monthly rotation.",
    valueProps: [
      { title: "After-Hours Service", body: "Treatment happens after close. Zero disruption to service, zero customer visibility." },
      { title: "Health Inspection Prep", body: "48-hour rush service before scheduled inspections. Full documentation for your file." },
      { title: "Fruit Fly + Drain Fly Programs", body: "Kitchen-specific IPM protocols for the pests that actually kill restaurant reviews." },
      { title: "Full Service Log", body: "Signed EPA-compliant records for every visit. Health department ready." },
    ],
    painpoints: [
      "Line cook spots a roach mid-shift and everything goes sideways",
      "Health inspection in 3 days and your last exterminator ghosted",
      "Fruit flies in the bar zone driving Yelp reviews down",
      "Rodent activity in dry storage right before a busy weekend",
    ],
    testimonial: {
      quote:
        "We had a rodent issue in dry storage. They came at 11pm the same night, treated, and had documentation on my desk by open. Health inspector passed us the next week.",
      author: "Owner · South Side restaurant",
    },
    formFields: {
      companyLabel: "Restaurant Name",
      volumeLabel: "Number of locations",
      volumeOptions: ["1", "2-3", "4-8", "8+"],
    },
    ctaHeadline: "Keep your kitchen. Keep your rating.",
    ctaSub: "Free walkthrough during your prep hours. No pressure.",
  },

  daycares: {
    slug: "daycares",
    code: "B2B-04",
    Icon: Baby,
    label: "For Daycares & Preschools",
    h1: "Daycare Pest Control in Pittsburgh",
    tagline: "Child-safe IPM. Fully documented. Parent-notified.",
    metaTitle: "Daycare Pest Control Pittsburgh · IPM Certified · Blue Collar World",
    metaDescription: "Child-safe pest management for Pittsburgh daycares, preschools, and youth programs. PA Act 36 compliant, IPM protocols, low-toxicity methods. Call 484-764-3392.",
    hero:
      "One pest complaint from a parent can shut a daycare's enrollment down. Our Integrated Pest Management (IPM) program uses child-safe protocols, PA Act 36-compliant notification, and low-toxicity methods designed specifically for youth environments.",
    valueProps: [
      { title: "IPM-First Approach", body: "Sanitation, exclusion, and monitoring stations before ANY chemical treatment." },
      { title: "PA Act 36 Compliance", body: "Full parent notification support, service logs, EPA labels, SDS sheets — audit-ready." },
      { title: "Off-Hours Treatment", body: "Weekend/evening visits so no children are present during any application." },
      { title: "Low-Toxicity Materials", body: "When treatment is required, we use the lowest-toxicity options approved for youth spaces." },
    ],
    painpoints: [
      "Ant activity in the snack area triggering parent complaints",
      "Wasp nest near the playground during pickup",
      "Mice in a storage room — enrollment paranoia kicks in",
      "State compliance paperwork missing when licensing visits",
    ],
    testimonial: {
      quote:
        "They document everything, show up on weekends only, and never made us fill out a form. We haven't had a parent complaint about pests in over two years.",
      author: "Director · Pittsburgh preschool (60 students)",
    },
    formFields: {
      companyLabel: "Facility Name",
      volumeLabel: "Approximate enrollment",
      volumeOptions: ["<20", "20-50", "50-100", "100+"],
    },
    ctaHeadline: "Child-safe. Parent-approved. Enrollment-protected.",
    ctaSub: "Free IPM assessment for licensed daycares and preschools.",
  },

  salons: {
    slug: "salons",
    code: "B2B-05",
    Icon: Scissors,
    label: "For Salons & Spas",
    h1: "Salon & Spa Pest Control in Pittsburgh",
    tagline: "Discreet. Product-safe. Client-friendly.",
    metaTitle: "Salon & Spa Pest Control Pittsburgh · Bed Bug & Roach Protection · Blue Collar World",
    metaDescription: "Discreet pest control for Pittsburgh hair salons, barbershops, nail salons, and day spas. Bed bug prevention, roach control, safe-for-product protocols. Call 484-764-3392.",
    hero:
      "Salons deal with pests other businesses don't — bed bugs from client belongings, roaches drawn to product storage, ants around wax pots. And every client who spots one tells everyone. We keep your space clean, discreetly.",
    valueProps: [
      { title: "Product-Safe Protocols", body: "Treatments don't affect shampoo, hair color, wax products, or nail chemicals." },
      { title: "After-Hours Service", body: "Treatment scheduled between clients or after close. Clients never see a service truck." },
      { title: "Bed Bug Prevention", body: "Monitors and quarterly inspections stop bed bugs before they hitchhike from client bags." },
      { title: "Reputation Protection", body: "Zero-signage service vehicles. Discreet appearance. Your clients never know." },
    ],
    painpoints: [
      "Client spots a roach near the wash basin",
      "Bed bug scare from a client's bag — panic in the DMs",
      "Ants marching to the sugar wax setup",
      "Rodent droppings in the back-of-house product storage",
    ],
    testimonial: {
      quote:
        "One time a client mentioned they thought they saw a bed bug in their coat. Blue Collar was here that night. Nothing found, whole place inspected, and my clients never knew a thing.",
      author: "Owner · Shadyside hair salon",
    },
    formFields: {
      companyLabel: "Salon / Spa Name",
      volumeLabel: "Number of stations / rooms",
      volumeOptions: ["1-5", "6-10", "11-20", "20+"],
    },
    ctaHeadline: "Clean space. Clean reputation.",
    ctaSub: "Free after-hours walkthrough. Same-day scheduling for urgent issues.",
  },

  hoas: {
    slug: "hoas",
    code: "B2B-06",
    Icon: Users,
    label: "For HOAs",
    h1: "HOA Pest Control Programs in Pittsburgh",
    tagline: "Community-wide programs. Board-friendly pricing.",
    metaTitle: "HOA Pest Control Pittsburgh · Community Programs · Blue Collar World",
    metaDescription: "Full-community pest control for Pittsburgh HOAs, condo associations, and planned communities. Quarterly common-area programs, member unit discounts, board reporting. Call 484-764-3392.",
    hero:
      "HOAs need consistent common-area service plus a reliable vendor residents can call directly for their own units. We run community-wide programs with tiered pricing, board-friendly reporting, and member discounts.",
    valueProps: [
      { title: "Common-Area Programs", body: "Quarterly service on shared spaces — pool decks, playgrounds, clubhouse, mailrooms." },
      { title: "Member Unit Discounts", body: "HOA residents get 15% off in-unit treatments. Handled directly — no board middleman needed." },
      { title: "Board Reporting", body: "Quarterly PDF reports for board meetings. Photos, treatment logs, activity trends." },
      { title: "Master Contract, Simple Terms", body: "One-page agreement. No hidden auto-renew traps. Cancel anytime." },
    ],
    painpoints: [
      "Board complaints about wasps at the pool",
      "Rodent activity in the mailroom — resident emails piling up",
      "Vendor charges surprise fees between quarterly visits",
      "No reporting for annual meetings when residents ask what's being done",
    ],
    testimonial: {
      quote:
        "Their quarterly board report is the best thing we've ever gotten from a vendor. Residents feel taken care of and I look organized at every meeting.",
      author: "HOA Board President · Bethel Park community",
    },
    formFields: {
      companyLabel: "HOA / Association Name",
      volumeLabel: "Number of units in community",
      volumeOptions: ["<50", "50-150", "150-400", "400+"],
    },
    ctaHeadline: "Happier residents. Cleaner community.",
    ctaSub: "Free proposal for your next board meeting. We'll walk the property first.",
  },

  landlords: {
    slug: "landlords",
    code: "B2B-07",
    Icon: Key,
    label: "For Landlords",
    h1: "Small Landlord Pest Control in Pittsburgh",
    tagline: "Multi-property discounts. Fast turnover treatments.",
    metaTitle: "Landlord Pest Control Pittsburgh · Multi-Property Rates · Blue Collar World",
    metaDescription: "Pest control for Pittsburgh small landlords and rental property investors. Multi-property discounts, fast turnover treatments, tenant coordination. Call 484-764-3392.",
    hero:
      "You own 3, 8, 20 doors across Pittsburgh — and a bed bug or roach call from a tenant means either eating the cost or losing the tenant. We handle small landlord portfolios with per-property pricing and turnover-fast treatments.",
    valueProps: [
      { title: "Multi-Property Discount", body: "Owning 3+ rentals unlocks portfolio rates — up to 25% off retail." },
      { title: "Turnover Treatments", body: "Between-tenant flush treatments so the next tenant walks into a clean unit. 48-hour scheduling." },
      { title: "Tenant Direct Line", body: "Give tenants our number for pest complaints. We handle it, bill you, no drama." },
      { title: "Written Guarantees", body: "Bed bug + rodent treatments come with 30-day written warranties you can show tenants." },
    ],
    painpoints: [
      "Tenant threatens to break lease over roaches",
      "Bed bugs on the move-out inspection — new tenant lease at risk",
      "Vendor charges retail rates on every single door individually",
      "Coordinating exterminator visits across 8 tenants who never answer texts",
    ],
    testimonial: {
      quote:
        "I own 12 doors around Pittsburgh. They give me one number, one invoice, and a discount. When my tenants call, they get service — I don't even hear about half of it anymore.",
      author: "Landlord · 12-door Pittsburgh portfolio",
    },
    formFields: {
      companyLabel: "Landlord Name / LLC",
      volumeLabel: "Number of rental units owned",
      volumeOptions: ["1-3", "4-10", "11-25", "25+"],
    },
    ctaHeadline: "One vendor. All your properties. Better rates.",
    ctaSub: "Send us your address list. We'll price it out property-by-property.",
  },

  "airbnb-hosts": {
    slug: "airbnb-hosts",
    code: "B2B-08",
    Icon: Bed,
    label: "For Airbnb Hosts",
    h1: "Airbnb & Short-Term Rental Pest Control in Pittsburgh",
    tagline: "Bed bug guarantee. Between-guest turnaround.",
    metaTitle: "Airbnb Pest Control Pittsburgh · Bed Bug Guarantee · Blue Collar World",
    metaDescription: "Short-term rental pest control for Pittsburgh Airbnb, VRBO, and STR hosts. Between-guest treatments, bed bug guarantee, discreet service. Call 484-764-3392.",
    hero:
      "One bad guest review about bed bugs and your Superhost status is done. We know STR hosts don't have time for a week-long treatment window between check-outs. We treat, guarantee, and turn your unit around in 24-48 hours.",
    valueProps: [
      { title: "Bed Bug Guarantee", body: "Written 30-day warranty on all bed bug treatments. Bugs return? We come back free." },
      { title: "24-48 Hour Turnaround", body: "Between guests? We can inspect, treat, and clear your unit before the next check-in." },
      { title: "Discreet Service", body: "Unmarked vehicles. Fast in/out. Nothing to alarm neighbors or building management." },
      { title: "Preventive Inspections", body: "Monthly or quarterly monitoring stations catch problems before they become reviews." },
    ],
    painpoints: [
      "Guest DMs 'I think I saw a bed bug' — refund panic starts",
      "Next check-in is in 36 hours and your usual vendor can't come",
      "One bad pest review kills your search ranking for months",
      "Multiple properties, no time to coordinate treatments across all of them",
    ],
    testimonial: {
      quote:
        "I have 4 short-term rentals downtown. They inspect quarterly, and if I ever get a guest complaint they're there in 24 hours. My Superhost status is intact, and I sleep better.",
      author: "Airbnb Host · 4 downtown Pittsburgh STRs",
    },
    formFields: {
      companyLabel: "Host Name / Business",
      volumeLabel: "Number of rental units",
      volumeOptions: ["1", "2-4", "5-10", "10+"],
    },
    ctaHeadline: "Protect your reviews. Protect your Superhost.",
    ctaSub: "Preventive inspection package for STR hosts. Includes bed bug guarantee.",
  },
};

export const b2bList = Object.values(b2bVerticals);
